import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Loop,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Sub-components
import { LoadingState } from './components/LoadingState';
import { NonRecurringState } from './components/NonRecurringState';
import { EmptyHistoryState } from './components/EmptyHistoryState';
import { RecurrenceHistoryTable } from './components/RecurrenceHistoryTable';
import { ChainInfoAlert } from './components/ChainInfoAlert';

const DEFAULT_PAGE_SIZE = 25;

const TaskRecurrenceHistory = React.memo(function TaskRecurrenceHistory({
  taskId,
  task,
  doUpdate,
  loadRecurrenceHistory,
  taskRecurrenceHistory,
  recurrenceLoading,
  recurrenceError,
  clearRecurrenceError
}) {
  const { t } = useTranslation(['common', 'tasks']);
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const load = useCallback((model) => {
    loadRecurrenceHistory({
      limit: model.pageSize,
      offset: model.page * model.pageSize,
    });
  }, [loadRecurrenceHistory]);

  // On taskId or doUpdate change: reset to page 0 and reload
  useEffect(() => {
    if (!taskId) return;
    const resetModel = { page: 0, pageSize: paginationModel.pageSize };
    setPaginationModel(resetModel);
    load(resetModel);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, doUpdate, load]);

  const handlePaginationModelChange = useCallback((newModel) => {
    setPaginationModel(newModel);
    load(newModel);
  }, [load]);

  const handleRefresh = useCallback(() => {
    load(paginationModel);
  }, [load, paginationModel]);

  const handleViewTask = useCallback((selectedTaskId) => {
    navigate(`/tasks/${selectedTaskId}?tab=recurrence`);
  }, [navigate]);

  if (recurrenceLoading && !taskRecurrenceHistory) {
    return <LoadingState />;
  }

  if (!task?.is_recurring && !taskRecurrenceHistory) {
    return <NonRecurringState />;
  }

  const totalChainLength = taskRecurrenceHistory?.total_chain_length ?? 0;

  return (
    <Box>
      {/* Error Alert */}
      {recurrenceError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearRecurrenceError}>
          {recurrenceError}
        </Alert>
      )}

      {/* Controls */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <Loop color="primary" />
          {t('tasks:recurrence_history.title')}
          {taskRecurrenceHistory && ' ' + t('tasks:recurrence_history.chain_count', {
            count: totalChainLength
          })}
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title={t('tasks:actions.refresh')}>
            <IconButton size="small" onClick={handleRefresh} disabled={recurrenceLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      {taskRecurrenceHistory?.chain_tasks?.length === 0 ? (
        <EmptyHistoryState />
      ) : (
        <>
          <RecurrenceHistoryTable
            chainTasks={taskRecurrenceHistory?.chain_tasks}
            currentTaskId={taskId}
            onViewTask={handleViewTask}
            totalCount={totalChainLength}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            loading={recurrenceLoading}
          />

          {taskRecurrenceHistory && (
            <ChainInfoAlert
              chainLength={totalChainLength}
              taskTitle={task?.title}
            />
          )}
        </>
      )}
    </Box>
  );
});

export default TaskRecurrenceHistory;
